import styled from 'styled-components';
import { ProductDTO } from 'src/models/pim';
import { Heading } from 'src/components/common/Heading';
import { useTranslateItem } from 'src/hooks/directus/useTranslateItem';
import { Markdown } from 'vendor/mods/markdown/components/Markdown';
import { Paragraph } from 'src/components/common/typography/Text';
import { FDA } from 'src/components/icons/FDA';
import { ProductAttributeCode } from 'src/models/pim/attributes';
import { useProductAttributes } from 'src/modules/products/hooks/useProductAttributes';
import { FC, ReactElement, ReactNode } from 'react';
import { FDARegistered } from 'src/components/icons/FDARegistered';
import { FDACleared } from 'src/components/icons/FDACleared';
import { head } from 'lodash';

export interface ProductPageShowcaseDetailsProps {
  product: ProductDTO;
  children: ReactNode;
}

const Container = styled.div`
  position: relative;
  width: 100%;
  max-width: 410px;
  color: ${({ theme }) => theme.text.palette.primary};
`;

const Description = styled(Paragraph)`
  margin: 24px 0;
`;

const Highlight = styled(Paragraph).attrs({ as: 'span' })`
  display: block;
  font-weight: 500;
  text-transform: uppercase;
  color: ${({ theme }) => theme.text.palette.accent};

  margin-bottom: 16px;
`;

const IconContainer = styled.span`
  display: block;
  margin-top: 16px;
`;

const fdaIconMap: Record<string, ReactElement> = {
  fda: <FDA width={78} />,
  'fda-registered': <FDARegistered width={78} />,
  'fda-cleared': <FDACleared width={78} />,
};

export const ProductPageShowcaseDetails: FC<
  ProductPageShowcaseDetailsProps
> = ({ product: _product, children }) => {
  const product = useTranslateItem(_product);
  const { getProductAttribute } = useProductAttributes(product.attributes);

  const description = product.description_long || product.description_short;

  const fdaIcon = head(getProductAttribute(ProductAttributeCode.FdaIcon));

  return (
    <Container>
      <Heading>
        <span>{product.name}</span>

        {product.tagline && <Paragraph>{product.tagline}</Paragraph>}
      </Heading>

      <Description>
        {product.function && <Highlight>{product.function}</Highlight>}

        {description && <Markdown>{description}</Markdown>}

        {!!fdaIcon && (
          <IconContainer>
            {fdaIconMap[fdaIcon.code ?? ''] ?? fdaIconMap.fda}
          </IconContainer>
        )}
      </Description>

      {children}
    </Container>
  );
};
